import { prettyDate, share, toNumber } from "./format";

const sum = (rows) => rows.reduce((total, row) => total + toNumber(row.value), 0);

const byName = (rows, name) => toNumber((rows || []).find((row) => row.name === name)?.value);

/* Geography is only worth charting once it resolves to real workplaces -
   a bar of one "Unassigned" row tells the reader nothing. */
const pickGeography = (regionSales, areaSales) => {
  const usable = (rows) => rows.filter((row) => row.name !== "Unassigned" && row.value > 0).slice(0, 8);
  const regions = usable(regionSales);
  if (regions.length) return { rows: regions, dimension: "Region" };
  return { rows: usable(areaSales), dimension: "Area" };
};

/**
 * Demand sheet pipeline. `conversion` is measured in money - invoiced value
 * against demanded value - which is what the API reports; the per-line
 * quantity view lives on the demand sheet itself.
 */
const selectDemand = (block) => {
  const conversion = block?.conversion || {};
  const demanded = toNumber(conversion.demanded);
  const invoiced = toNumber(conversion.invoiced);

  return {
    kpi: block?.kpi || {},
    status: block?.status || {},
    backlog: block?.backlog || {},
    conversion: { demanded, invoiced, open: toNumber(conversion.open), orders: toNumber(conversion.orders) },
    fulfilmentRate: share(invoiced, demanded),
    trend: block?.trend || [],
    ageing: (block?.ageing || []).filter((row) => row.value > 0),
    openSheets: block?.open_sheets || [],
    hasActivity: toNumber(block?.status?.sheets) > 0,
  };
};

/**
 * Shapes the /dashboard/admin payload into what the page renders.
 * Pure and null-safe: `data` may be null while the first request is in flight.
 */
export const selectDashboard = (data, range) => {
  const kpi = data?.kpi || {};
  const delivery = data?.delivery || {};
  const payment = data?.payment || {};
  const catalog = data?.catalog || {};

  const delivered = toNumber(delivery.delivered?.value);
  const pending = toNumber(delivery.pending?.value);
  const paid = byName(payment.by_status, "Paid");
  const unpaid = byName(payment.by_status, "Unpaid");

  const demand = selectDemand(data?.demand);

  const geography = pickGeography(data?.region_sales || [], data?.area_sales || []);
  const topProducts = data?.top_products || [];
  const lastDate = data?.bounds?.last_date || null;
  const hasActivity = toNumber(kpi.orders?.value) > 0;

  return {
    kpi,
    catalog,
    trend: data?.trend || [],
    granularity: data?.range?.granularity === "day" ? "day" : "month",
    comparison: data?.range ? `vs ${prettyDate(data.range.previous_from)} – ${prettyDate(data.range.previous_to)}` : "",
    collectionRate: share(toNumber(kpi.collection?.value), toNumber(kpi.sales?.value)),

    delivery: {
      delivered,
      pending,
      deliveredOrders: toNumber(delivery.delivered?.orders),
      pendingOrders: toNumber(delivery.pending?.orders),
    },
    payment: { paid, unpaid, byType: (payment.by_type || []).slice(0, 4) },

    demand,
    ageing: (data?.outstanding_ageing || []).filter((row) => row.value > 0),
    geography: { ...geography, total: sum(geography.rows) },
    topProducts: { rows: topProducts, total: sum(topProducts) },
    topDistributors: data?.top_distributors || [],
    recentOrders: data?.recent_orders || [],

    lastDate,
    // Nothing billed because the window lies after the newest invoice on file.
    outsideData: Boolean(data) && !hasActivity && Boolean(lastDate) && lastDate < range.from,
  };
};
